import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for displaying and updating a timer for the current survey question.
 * Handles timer start, update, and persistence using localStorage.
 *
 * @class TimerController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Values for question ID and timer start/end times.
   * @type {{currentQuestionId: Number, startTime: String, endTime: String}}
   */
  static values = {
    currentQuestionId: {
      type: Number,
      default: 1
    },
    startTime: String,
    endTime: String,
    storageKeyNamespace: { type: String, default: "" }
  };

  /**
   * Called when the controller is connected to the DOM.
   * Sets the start time, updates the timer, and starts the interval if needed.
   */
  connect() {
    this.setStartTime();
    this.updateTime();
    if (!this.endTimeValue) {
      this.startTimer();
    }
  }

  /**
   * Sets the start time in localStorage if not already set or if question changes.
   * Ensures timer is per-question and persists across reloads.
   */
  setStartTime() {
    const ns = this.storageKeyNamespaceValue ? `${this.storageKeyNamespaceValue}_` : "";
    const questionKey = `${ns}currentquestionid`;
    const startKey = `${ns}starttime`;
    const storedQuestionId = localStorage.getItem(questionKey);
    const storedStartTime = localStorage.getItem(startKey);
    // If no stored values, or if question or start time changed, update localStorage
    if (!storedQuestionId || !storedStartTime || this.startTimeValue != storedStartTime || this.currentQuestionIdValue != storedQuestionId) {
      if (this.hasStartTimeValue) {
        localStorage.setItem(startKey, this.startTimeValue);
      }
      localStorage.setItem(questionKey, this.currentQuestionIdValue);
    }
  }

  /**
   * Starts the timer interval to update the display every second.
   */
  startTimer() {
    this.interval = setInterval(() => {
      this.updateTime();
    }, 1000);
  }

  /**
   * Updates the timer display based on elapsed time since start.
   * Uses endTime if present, otherwise uses current time.
   */
  updateTime() {
    const now = this.hasEndTimeValue ? new Date(this.endTimeValue) : new Date();
    const ns = this.storageKeyNamespaceValue ? `${this.storageKeyNamespaceValue}_` : "";
    const startKey = `${ns}starttime`;
    const startTime = localStorage.getItem(startKey) ? new Date(localStorage.getItem(startKey)) : new Date();
    const elapsedSeconds = Math.floor((now - startTime) / 1000);
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;
    this.element.textContent = this.stringifyTime(hours, minutes, seconds);
  }

  /**
   * Pads a number to two digits for display.
   * @param {number} number
   * @return {string}
   */
  pad(number) {
    return number.toString().padStart(2, '0');
  }

  /**
   * Formats the elapsed time as HH:MM:SS or MM:SS.
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   * @return {string}
   */
  stringifyTime(hours, minutes, seconds) {
    if (hours > 0) {
      return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    } else {
      return `${minutes}:${this.pad(seconds)}`;
    }
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Clears the timer interval to prevent memory leaks.
   */
  disconnect() {
    clearInterval(this.interval);
  }
}
